/**
 * @file UrlHandler.js
 *
 * Handles the construction and parsing of search Urls.
 * This class only contains static methods and therefore
 * does not need a constructor.
 *
 * This class is responsible for the encoding and decoding of urls.
 * Do not, therefore, try to encode or decode in other classes as it will
 * cause problems.
 *
 * @author Stanley Clark <stanley@studyportals.com>
 * @author Steve Zeeuwe <steve@studyportals.com>
 * @author Rob van den Hout <vdhout@studyportals.com>
 * @copyright © 2017-2019 Studyportals B.V., all rights reserved.
 * @version 1.2.0
 */
import Parameters from '../Parameters.js';

export class UrlHandler {
	/**
	 * Construct a search url based on the given parameters
	 *
	 * Keywords are ENCODED in this function
	 *
	 * @param {Object} SearchParameters
	 * @param {boolean} aliased If the provided q parameters are already aliased
	 * @return {String}
	 */

	// eslint-disable-next-line no-unused-vars
	static constructSearchUrl(SearchParameters, aliased = false) {
		// Assume that the first question mark in the URL is the start of the
		// get parameter string
		const urlParts = window.location.href.split('?');

		// Assume that the first hash in the get parameters is part of the hash
		// and not an intentional parameter character
		const previousParams = urlParts.length > 1 ? urlParts[1].split('#')[0] : '';

		// Needed to transform keywords from kw-what/kw-where that are matching existing areas or disciplines
		const searchParams = UrlHandler.transformSearchParams(SearchParameters.q);
		if (searchParams && Object.keys(searchParams).length > 0) {
			const aliasedParams = Parameters.alias(searchParams);
			SearchParameters.search = aliasedParams;

			if (searchParams['country_ids'] || searchParams['organisation_ids'] || searchParams['region_ids']) {
				delete SearchParameters['kw-where'];
			}
			if (searchParams['discipline_ids']) {
				delete SearchParameters['kw-what'];
			}
		}

		// remove parameters that are only needed in old SearchPage
		delete SearchParameters['levels'];
		delete SearchParameters['q'];

		const baseURL = UrlHandler.getCurrentSearchUrl();
		const joined = UrlHandler._joinSearchParameters(SearchParameters);

		const origin = window.location.origin;

		let searchQuery = '';

		if (joined.length > 0) {
			searchQuery = `?${joined}`;
			if (previousParams !== '') {
				searchQuery = searchQuery + `&${previousParams}`;
			}
		} else {
			searchQuery = `?${previousParams}`;
		}

		let portalType = 'master';
		if (origin.indexOf('phd') >= 0) {
			portalType = 'phd';
		}
		if (origin.indexOf('bachelor') >= 0) {
			portalType = 'bachelor';
		}
		if (origin.indexOf('shortcourses') >= 0) {
			portalType = 'course';
		}
		if (origin.indexOf('distancelearning') >= 0) {
			portalType = 'distance-learning?mh=online,blended';
			if (searchQuery.length > 1) {
				searchQuery = searchQuery.replace('?', '&');
			} else {
				searchQuery = '';
			}
		}
		return `${baseURL}${portalType}${searchQuery}`;
	}

	/**
	 * Construct a q parameter string based on the given parameters
	 *
	 * The keywords are encoded before the string is created
	 *
	 * @param {Object} qParams
	 * @param {boolean} aliased If the provided q parameters are already aliased
	 * @returns {string|null}
	 */

	static constructShortQString(qParams, aliased) {
		if (!qParams) {
			return null;
		}

		let qString = '';

		if (aliased) {
			UrlHandler._encodeParameter(qParams, 'kw');
			qString = UrlHandler.createQParamsString(qParams);
		} else {
			UrlHandler._encodeParameter(qParams, 'q');
			qString = Parameters.alias(qParams);
		}
		return qString;
	}

	/**
	 * Create a q parameter string from already aliased parameters
	 * @param {Object} qParams
	 * @returns {string}
	 */

	static createQParamsString(qParams) {
		return Object.keys(qParams)
			.filter((k) => qParams[k])
			.map((k) => `${k}-${qParams[k]}`)
			.join('|');
	}

	/**
	 * Transform search parameter so that registered keywords are transform to corresponding alias
	 * @param {Object} qParams
	 * @returns {object|null}
	 */

	static transformSearchParams(qParams) {
		if (!qParams) return null;

		const params = {};

		UrlHandler._encodeParameter(qParams, 'q');
		const aliasedParams = Parameters.alias(qParams, 'q');
		const unaliasedParams = Parameters.unalias(aliasedParams);

		Object.keys(unaliasedParams).forEach((param_key) => {
			if (
				param_key === 'discipline_ids' ||
				param_key === 'country_ids' ||
				param_key === 'organisation_ids' ||
				param_key === 'region_ids' ||
				param_key === 'degrees'
			) {
				params[param_key] = qParams[param_key];
			}
		});

		return params;
	}

	/**
	 * Take each part of the search parameters and join them together with &
	 *
	 * @param {object} searchParameters
	 * @returns {string}
	 * @private
	 */

	static _joinSearchParameters(searchParameters) {
		return Object.keys(searchParameters)
			.filter((param) => searchParameters[param])
			.map((param) => `${param}=${searchParameters[param]}`)
			.join('&');
	}

	/**
	 * Encode a parameter, in place, if it exists in the passed in q parameters
	 * @param {Object} qParams
	 * @param {string} paramName
	 * @private
	 */

	static _encodeParameter(qParams, paramName) {
		if (!qParams[paramName]) return;

		qParams[paramName] = encodeURIComponent(qParams[paramName]);
	}

	/**
	 * Get the current search URL depending on what search page you are on.
	 *
	 * @returns {string}
	 */

	static getCurrentSearchUrl() {
		let destination = window.location.origin;

		if (window.Loot && window.Loot.DataStorage) {
			// eslint-disable-next-line no-undef
			const userPortalPreference = Loot.DataStorage.retrieve('UserPortalPreference');
			if (userPortalPreference && UrlHandler.isArticlePage()) {
				destination = userPortalPreference;
			}
		}

		if (!UrlHandler.isSearchPage()) {
			// Return the default url when a non-recognised base URL is provided
			return `${destination}/search/`;
		}

		// Return the current page without query or hash
		return destination + window.location.pathname;
	}

	/**
	 * Gets the hash of the current page from the URL
	 *
	 * @return {String}
	 */

	static getPageHash() {
		return window.location.hash;
	}

	/**
	 * Check if there are query parameters in the url and replace them in the page hash
	 */

	static replaceQueryParametersInURL() {
		// Add support for query parameters
		const url = new URL(window.location);
		let searchParams = new URLSearchParams(url.search);
		if (searchParams && searchParams.has('q')) {
			let hash = '#q=' + searchParams.get('q');
			searchParams.delete('q');
			window.history.replaceState({}, document.title, url.pathname + hash + '?' + searchParams);
		}
	}

	/**
	 * Takes a location hash and returns an object containing the parameters
	 *
	 * Keywords are DECODED here.
	 *
	 * @param {String} hashValue
	 * @return {{URLSearchParams, String}}
	 */

	static parseSearchHash(hashValue) {
		// Make sure that a valid hash is present, otherwise don't do anything

		if (hashValue === '' || hashValue.charAt(0) !== '#') {
			return {
				searchParameters: new URLSearchParams(),
				hash: ''
			};
		}

		// Remove the first character which is by now known to be the #

		const searchParameters = UrlHandler._getSearchParams(hashValue.slice(1));

		return {
			searchParameters: searchParameters,
			hash: hashValue
		};
	}

	/**
	 * Navigate the browser to the particular URL given by setting the location
	 *
	 * @param {String} url
	 */

	static setUrl(url) {
		window.location = url;
	}

	/**
	 * Constructs and sets a search URL based on the given searchRequest instance
	 *
	 * @param {SearchRequest} searchRequest
	 */

	static updateSearchUrl(searchRequest) {
		const searchParams = searchRequest.getSearchParameters();
		const searchUrl = UrlHandler.constructSearchUrl(searchParams);
		UrlHandler.setUrl(searchUrl);
	}

	/**
	 * Omits encoding URL characters from the specified hash and returns params
	 *
	 * Special character found are replaced by ''.
	 * URL Parameters are also all automatically decoded
	 *
	 * @param {string} hash
	 * @return {URLSearchParams}
	 */

	static _getSearchParams(hash) {
		// Remove all % not followed by two digits (or %25, the encoded variant)
		// Remove all & not followed by a key and = (&something=something)

		hash = hash.replace(/%25/gi, '').replace(/&(?![a-z]*[=])/gi, '');
		hash = hash.split('?')[0];
		return new URLSearchParams(hash);
	}

	/**
	 * Omits special URL characters from the specified text.
	 *
	 * Special character found are replaced by ''.
	 * Is used when encoding a URL.
	 *
	 * @param {String} text
	 * @return {String}
	 */

	static omitURLSpecialCharacters(text) {
		// eslint-disable-next-line no-useless-escape
		return text.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '');
	}

	/**
	 * Determine if the current page is the search page
	 *
	 * @return {Boolean}
	 */

	static isSearchPage() {
		const path = window.location.origin + '/search/';
		return window.location.href.indexOf(path) > -1;
	}

	/**
	 * Determine if the current page is an article page
	 *
	 * @return {Boolean}
	 */

	static isArticlePage() {
		const path = window.location.origin + '/articles/';
		return window.location.href.indexOf(path) > -1;
	}

	/**
	 * Determine if the current page is the study page
	 *
	 * @return {Boolean}
	 */

	static isStudyPage() {
		let isStudy = false;
		for (let path of ['/studies/']) {
			if (window.location.href.indexOf(path) > -1) {
				isStudy = true;
				break;
			}
		}

		return isStudy;
	}

	/**
	 * Determine if the current page is the organisation page
	 *
	 * @return {Boolean}
	 */

	static isOrganisationPage() {
		let isOrganisation = false;
		for (let path of ['/universities/']) {
			if (window.location.href.indexOf(path) > -1) {
				isOrganisation = true;
				break;
			}
		}

		return isOrganisation;
	}

	/**
	 * Construct the url of the search page by giving an object containing all
	 * parameters. This function is defined to allow the construction of the
	 * desired url construction for the analysis in Google Analytics.
	 *
	 * @param data {Object}
	 * @return {string}
	 */

	static constructGASearchParameters(data) {
		const searchParameters = Parameters.alias(data.parameters);

		// remove the parameters which are not part of the url

		const omitParams = ['q', 'token', 'path', 'hh', 'include_order'];

		omitParams.forEach((param) => {
			delete data.extra[param];
		});

		// add the length parameter

		data.extra.length = 10;

		const urlParameters = Object.keys(data.extra)
			.map((key) => {
				return key + '=' + data.extra[key];
			})
			.join('&');

		return '#q=' + searchParameters + '&' + urlParameters;
	}
}

window.UrlHandler = UrlHandler;
